import { inject } from '@angular/core'

import { Actions, createEffect, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { catchError, filter, map, mergeMap, of, switchMap, withLatestFrom } from 'rxjs'

import { NodeSlicesActions } from '@flowx/designer/shared/store/actions/node-slices.actions'

import { NodeApiService } from '../../api/node.api'
import { ToastMessageService } from '../../services/toast-message.service'
import { selectAllNodesSlices } from '../selectors/node-slices.selectors'

export const loadNodeSlices$ = createEffect(
  (
    actions$ = inject(Actions),
    toastMessageService = inject(ToastMessageService),
    nodeApi = inject(NodeApiService),
    store = inject(Store)
  ) =>
    actions$.pipe(
      ofType(NodeSlicesActions.getNodeSlices),
      withLatestFrom(store.select(selectAllNodesSlices)),
      filter(([{ nodeFlowxUuid }, nodeSlices]) => !nodeSlices[nodeFlowxUuid]),
      switchMap(([{ nodeFlowxUuid }]) =>
        nodeApi.getResourceDataSlices(nodeFlowxUuid).pipe(
          map(({ resourceContent }) =>
            NodeSlicesActions.getNodeSlicesSuccess({
              nodeSlices: resourceContent,
              nodeFlowxUuid,
            })
          ),
          catchError((err) => {
            toastMessageService.showErrorMessage(err?.error?.title ?? 'Failed to get node slices')
            return of(NodeSlicesActions.getNodeSlicesFailure({ nodeFlowxUuid }))
          })
        )
      )
    ),
  {
    functional: true,
  }
)

export const createNodeSlice$ = createEffect(
  (
    actions$ = inject(Actions),
    toastMessageService = inject(ToastMessageService),
    nodeApi = inject(NodeApiService)
  ) =>
    actions$.pipe(
      ofType(NodeSlicesActions.createNodeSlice),
      mergeMap(({ nodeFlowxUuid, sliceType, sliceItems }) =>
        nodeApi.createResourceDataSlice(nodeFlowxUuid, { type: sliceType, sliceItems }).pipe(
          map(({ resourceContent }) =>
            NodeSlicesActions.createNodeSliceSuccess({
              nodeSlice: resourceContent,
              nodeFlowxUuid,
            })
          ),
          catchError((err) => {
            toastMessageService.showErrorMessage(err?.error?.title ?? 'Failed to create node slice')
            return of(NodeSlicesActions.createNodeSliceFailure({ nodeFlowxUuid }))
          })
        )
      )
    ),
  {
    functional: true,
  }
)

export const updateNodeSlice$ = createEffect(
  (
    actions$ = inject(Actions),
    toastMessageService = inject(ToastMessageService),
    nodeApi = inject(NodeApiService)
  ) =>
    actions$.pipe(
      ofType(NodeSlicesActions.updateNodeSlice),
      mergeMap(({ nodeFlowxUuid, slice }) =>
        nodeApi.updateResourceDataSlice(nodeFlowxUuid, slice).pipe(
          map(({ resourceContent }) =>
            NodeSlicesActions.updateNodeSliceSuccess({
              nodeSlice: resourceContent,
              nodeFlowxUuid,
            })
          ),
          catchError((err) => {
            toastMessageService.showErrorMessage(err?.error?.title ?? 'Failed to update node slice')
            return of(NodeSlicesActions.updateNodeSliceFailure({ nodeFlowxUuid }))
          })
        )
      )
    ),
  {
    functional: true,
  }
)
